/**
 * Centered popup row editor for Shipment Costing.
 *
 * Replaces the in-row inline editor with a roomy vertical form.
 * Auto-recalc is OPT-IN via a toggle/button so the user's manual
 * row_weight / qty values aren't clobbered server-side.
 */
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, X, RefreshCw, CalendarDays, Search } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_KEYS = [
  "client_code",
  "order_date",
  "detected_service",
  "tracking",
  "detected_country",
  "product",
  "quantity",
  "row_weight",
  "gross_weight",
];

// Convert "DDMMYY" → "YYYY-MM-DD" for native date input
const ddmmyyToISO = (v) => {
  if (!v || v.length !== 6) return "";
  const dd = v.slice(0, 2), mm = v.slice(2, 4), yy = v.slice(4, 6);
  return `20${yy}-${mm}-${dd}`;
};
// Reverse
const isoToDDMMYY = (iso) => {
  if (!iso || iso.length < 10) return "";
  const [y, m, d] = iso.split("-");
  return `${d}${m}${y.slice(-2)}`;
};

export const RowEditorDialog = ({
  row,
  open,
  onClose,
  token,
  adminPassword,
  onSaved,
  lookups = { clients: [], services: [], countries: [] },
  onProductSearch,
}) => {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [autoRecalc, setAutoRecalc] = useState(false);
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const productDebounceRef = useRef(null);

  // Re-seed values whenever the dialog opens for a new row
  useEffect(() => {
    if (open && row) {
      setValues({
        client_code: row.client_code || "",
        order_date: row.order_date || "",
        detected_service: row.detected_service || "",
        tracking: row.tracking || "",
        detected_country: row.detected_country || "",
        product: row.product || "",
        quantity: row.quantity ?? "",
        row_weight: row.row_weight ?? row.weight ?? "",
        gross_weight: row.gross_weight ?? row.display_gross_weight ?? row.parcel_gross_weight ?? "",
      });
      setShowSug(false);
      setAutoRecalc(false);
    }
  }, [open, row]);

  useEffect(() => () => {
    if (productDebounceRef.current) clearTimeout(productDebounceRef.current);
  }, []);

  const setField = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const handleProductInput = (v) => {
    setField("product", v);
    if (productDebounceRef.current) clearTimeout(productDebounceRef.current);
    if (v && v.length >= 3 && onProductSearch) {
      productDebounceRef.current = setTimeout(async () => {
        try {
          const r = await onProductSearch(v);
          setProductSuggestions(r.slice(0, 10));
          setShowSug(r.length > 0);
        } catch {
          setShowSug(false);
        }
      }, 200);
    } else {
      setShowSug(false);
    }
  };

  const handleSave = async () => {
    const original = {
      client_code: row.client_code || "",
      order_date: row.order_date || "",
      detected_service: row.detected_service || "",
      tracking: row.tracking || "",
      detected_country: row.detected_country || "",
      product: row.product || "",
      quantity: String(row.quantity ?? ""),
      row_weight: String(row.row_weight ?? row.weight ?? ""),
      gross_weight: String(row.gross_weight ?? row.display_gross_weight ?? row.parcel_gross_weight ?? ""),
    };
    const changes = {};
    for (const k of FIELD_KEYS) {
      const cur = (k === "quantity" || k === "row_weight" || k === "gross_weight") ? String(values[k] ?? "") : values[k];
      if (cur !== original[k]) changes[k] = values[k];
    }
    if (Object.keys(changes).length === 0) {
      toast.info("No changes to save");
      onClose?.();
      return;
    }
    setSaving(true);
    try {
      const recId =
        row.record_id || row._id || row.id ||
        (row.sheet_name && row.row_number != null && row.tracking
          ? `${row.sheet_name}|${row.row_number}|${row.tracking}` : null);
      if (!recId) throw new Error("Row has no identifier");
      const res = await fetch(`${BACKEND_URL}/api/shipment-costing/order/${recId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ admin_password: adminPassword, changes }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success(
        `Saved ${data.changed_fields?.length || 0} change(s)` +
          (data.cascade ? " · cascaded to parcel" : "") +
          (data.ship_recalc_done ? " · ship cost recalculated" : "")
      );
      // Auto-recalc only if user explicitly opted in
      if (autoRecalc && data.affected_tracking) {
        try {
          await fetch(`${BACKEND_URL}/api/shipment-costing/recalculate-by-tracking`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ tracking_numbers: [data.affected_tracking] }),
          });
          toast.success(`Recalculated costs for ${data.affected_tracking}`);
        } catch {}
      }
      onSaved?.(data);
    } catch (err) {
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const lbl = "block text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1";
  const inputCls = "w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="row-editor-dialog"
        onKeyDownCapture={(e) => e.stopPropagation()}
        onKeyPressCapture={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="text-indigo-700">Edit row</DialogTitle>
          <DialogDescription className="text-xs">
            {row?.tracking ? `${row.tracking} · order #${row.order_no || row.order_number || "—"} · ${row.sheet_name || ""}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* CLIENT */}
          <div>
            <label className={lbl}>Client code</label>
            <input
              list="row-editor-clients"
              value={values.client_code || ""}
              onChange={(e) => setField("client_code", e.target.value.toUpperCase())}
              className={inputCls}
              data-testid="re-client"
              autoComplete="off"
            />
            <datalist id="row-editor-clients">
              {lookups.clients.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          {/* DATE */}
          <div>
            <label className={lbl}>Date (DDMMYY)</label>
            <div className="flex gap-1">
              <input
                type="text"
                placeholder="DDMMYY"
                value={values.order_date || ""}
                onChange={(e) => setField("order_date", e.target.value.replace(/\D/g, "").slice(0, 6))}
                className={inputCls}
                maxLength={6}
                data-testid="re-date-text"
                autoComplete="off"
              />
              <input
                type="date"
                value={ddmmyyToISO(values.order_date)}
                onChange={(e) => setField("order_date", isoToDDMMYY(e.target.value))}
                className="px-2 py-2 text-sm border border-slate-300 rounded-md w-12"
                title="Pick from calendar"
                data-testid="re-date-cal"
              />
            </div>
          </div>
          {/* SERVICE */}
          <div>
            <label className={lbl}>Service</label>
            <select
              value={values.detected_service || ""}
              onChange={(e) => setField("detected_service", e.target.value)}
              className={inputCls}
              data-testid="re-service"
            >
              <option value="">— None —</option>
              {lookups.services.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <div className="text-[10px] text-slate-400 mt-0.5">{lookups.services.length} services</div>
          </div>
          {/* COUNTRY */}
          <div>
            <label className={lbl}>Country</label>
            <select
              value={values.detected_country || ""}
              onChange={(e) => setField("detected_country", e.target.value)}
              className={inputCls}
              data-testid="re-country"
            >
              <option value="">— None —</option>
              {lookups.countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="text-[10px] text-slate-400 mt-0.5">{lookups.countries.length} countries</div>
          </div>
          {/* TRACKING (full width) */}
          <div className="sm:col-span-2">
            <label className={lbl}>Tracking</label>
            <input
              value={values.tracking || ""}
              onChange={(e) => setField("tracking", e.target.value.toUpperCase())}
              className={inputCls}
              data-testid="re-tracking"
              autoComplete="off"
            />
            <div className="text-[10px] text-amber-700 mt-0.5">
              Changing tracking cascades parcel-level fields to all rows of the same parcel.
            </div>
          </div>
          {/* PRODUCT (full width) */}
          <div className="sm:col-span-2 relative">
            <label className={lbl}>Product (type 3+ chars for suggestions)</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={values.product || ""}
                onChange={(e) => handleProductInput(e.target.value)}
                onBlur={() => setTimeout(() => setShowSug(false), 150)}
                onFocus={() => values.product?.length >= 3 && productSuggestions.length && setShowSug(true)}
                className={`${inputCls} pl-9`}
                placeholder="e.g. TAP 100"
                data-testid="re-product"
                autoComplete="off"
              />
              {showSug && (
                <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-indigo-200 rounded-md shadow-lg max-h-60 overflow-auto" data-testid="re-product-suggestions">
                  {productSuggestions.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className="block w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50"
                      onMouseDown={(e) => { e.preventDefault(); setField("product", p); setShowSug(false); }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* QTY */}
          <div>
            <label className={lbl}>Quantity</label>
            <input
              type="number"
              min="0"
              value={values.quantity ?? ""}
              onChange={(e) => setField("quantity", e.target.value)}
              className={`${inputCls} text-right`}
              data-testid="re-qty"
              autoComplete="off"
            />
          </div>
          {/* ROW WT */}
          <div>
            <label className={lbl}>Row weight (g)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={values.row_weight ?? ""}
              onChange={(e) => setField("row_weight", e.target.value)}
              className={`${inputCls} text-right`}
              data-testid="re-row-wt"
              autoComplete="off"
            />
          </div>
          {/* GROSS WT (full row for clarity) */}
          <div className="sm:col-span-2">
            <label className={lbl}>Gross weight (g) — parcel-level</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={values.gross_weight ?? ""}
              onChange={(e) => setField("gross_weight", e.target.value)}
              className={`${inputCls} text-right`}
              data-testid="re-gross-wt"
              autoComplete="off"
            />
            <div className="text-[10px] text-slate-400 mt-0.5">
              Shipping cost is calculated against this weight (rounded up to 50g slab).
            </div>
          </div>
        </div>

        <div className="border-t pt-3 mt-2">
          <label className="flex items-start gap-2 text-xs cursor-pointer select-none" data-testid="re-autorecalc-toggle">
            <input
              type="checkbox"
              checked={autoRecalc}
              onChange={(e) => setAutoRecalc(e.target.checked)}
              className="mt-0.5"
            />
            <div>
              <div className="font-semibold text-slate-700">
                Re-calculate costs after save
              </div>
              <div className="text-[11px] text-amber-700">
                ⚠️ Recalc may overwrite your manual qty / row-weight using the product catalog's per-unit values. Leave OFF to preserve exactly what you typed.
              </div>
            </div>
          </label>
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="re-cancel">
            <X className="w-3.5 h-3.5 mr-1" /> Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="re-save"
          >
            {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RowEditorDialog;
